import { Stack, SimpleGrid, Title, Text, Button, Group } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { useQuery } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { useBillingStats } from '../../data/useStats';
import { useInvoices } from '../../data/useInvoices';
import { StatCard } from '../../components/StatCard';
import { BillingNavCard } from './components/BillingNavCard';
import {
  IconAlertFile, IconChart, IconClock, IconGateway, IconGlobe, IconInvoice,
  IconLayers, IconLedger, IconRefresh, IconSpark, IconTag, IconTrend, IconWallet
} from './components/icons';
import { formatInteger, formatMinor } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';
import './components/billing.css';

/** Three KPIs across, one on a phone: a fixed `cols={3}` squashed the cards below 768px. */
const GRID_COLS = { base: 1, sm: 2, lg: 3 };

export default function BillingOverview() {
  const t = useT();
  const billing = useBillingStats();
  const invoices = useInvoices();
  // Same query keys the sub-pages use, so on a warm cache the hub costs nothing.
  const subsQ = useQuery({ queryKey: ['admin', 'subscriptions'], queryFn: () => admin.listSubscriptions() });

  const wallets = billing.data?.data.wallets ?? [];
  const ledger7d = billing.data?.data.ledger_volume_7d ?? 0;
  const walletCount = wallets.reduce((sum, w) => sum + w.wallet_count, 0);

  // Two conditions the platform tracks on every row and surfaces nowhere else
  // in the console: an invoice the tax validator rejected, and a subscription
  // the renewal worker could not charge.
  const invalidInvoices = (invoices.data?.data ?? []).filter((i) => i.validation_status === 'invalid');
  const pastDueSubs = (subsQ.data?.data ?? []).filter((s) => s.status === 'past_due');

  /**
   * Totals are summed per currency and formatted once by `formatMinor`. Adding
   * cents of one currency to cents of another gives a number that means
   * nothing, so a mixed set renders as two figures rather than one wrong one.
   */
  function totalPerCurrency(rows: Array<{ total_minor: number; currency: string }>): string {
    const byCurrency = new Map<string, number>();
    for (const r of rows) byCurrency.set(r.currency, (byCurrency.get(r.currency) ?? 0) + r.total_minor);
    return Array.from(byCurrency.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([currency, minor]) => formatMinor(minor, currency))
      .join(' · ');
  }

  const walletTotals = wallets.map((w) => formatMinor(w.balance_minor_total, w.currency)).join(' · ');
  const invalidTotal = totalPerCurrency(invalidInvoices);

  // The freshest of the three queries: the header claims the data is this old,
  // so it has to be the oldest of what is on screen, not the newest.
  const updatedAt = Math.min(
    ...[billing.dataUpdatedAt, invoices.dataUpdatedAt, subsQ.dataUpdatedAt].filter((n) => n > 0)
  );
  const refreshing = billing.isFetching || invoices.isFetching || subsQ.isFetching;

  function refreshAll() {
    void billing.refetch();
    void invoices.refetch();
    void subsQ.refetch();
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.overview.eyebrow')}
        title={t('billing.overview.title')}
        description={t('billing.overview.description')}
        actions={
          <Group gap="sm" align="center" wrap="nowrap">
            {Number.isFinite(updatedAt) && (
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('billing.overview.updated', { time: new Date(updatedAt).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) })}
              </Text>
            )}
            <Button size="sm" variant="outline" leftSection={<IconRefresh />} loading={refreshing} onClick={refreshAll}>
              {t('billing.overview.refresh')}
            </Button>
          </Group>
        }
      />

      {billing.error && (
        <Alert color="error">
          <Stack gap="sm">
            <Text role="bodySmall">{t('billing.overview.loadError')}</Text>
            <Group>
              <Button size="xs" variant="outline" onClick={() => void billing.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Stack>
        </Alert>
      )}

      <SimpleGrid cols={GRID_COLS} spacing="md">
        <StatCard
          tone="primary" icon={<IconGlobe />}
          label={t('billing.overview.kpi.currencies')}
          value={formatInteger(wallets.length)}
          hint={walletTotals || t('billing.overview.kpi.currenciesHint')}
          loading={billing.isLoading}
        />
        <StatCard
          tone="success" icon={<IconTrend />}
          label={t('billing.overview.kpi.ledgerVolume')}
          value={formatInteger(ledger7d)}
          hint={t('billing.overview.kpi.ledgerEntries')}
          loading={billing.isLoading}
          to="/billing/ledger"
          linkLabel={t('billing.overview.link.ledger')}
        />
        <StatCard
          tone="info" icon={<IconWallet />}
          label={t('billing.overview.kpi.totalWallets')}
          value={formatInteger(walletCount)}
          hint={t('billing.overview.kpi.acrossCurrencies')}
          loading={billing.isLoading}
        />
      </SimpleGrid>

      <Stack gap="sm">
        <Stack gap="xs">
          <Title order={4}>{t('billing.overview.operations')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.overview.operationsHint')}</Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <StatCard
            tone="error" icon={<IconAlertFile />}
            label={t('billing.overview.kpi.invalidInvoices')}
            value={formatInteger(invalidInvoices.length)}
            hint={invalidTotal
              ? t('billing.overview.kpi.invalidTotal', { total: invalidTotal })
              : t('billing.overview.kpi.noInvalid')}
            loading={invoices.isLoading}
            to="/billing/invoices?validation=invalid"
            linkLabel={t('billing.overview.link.invoices')}
          />
          <StatCard
            tone="warning" icon={<IconClock />}
            label={t('billing.overview.kpi.pastDue')}
            value={formatInteger(pastDueSubs.length)}
            hint={t('billing.overview.kpi.pastDueHint')}
            loading={subsQ.isLoading}
            to="/billing/subscriptions?status=past_due"
            linkLabel={t('billing.overview.link.subscriptions')}
          />
        </SimpleGrid>
      </Stack>

      <SimpleGrid cols={GRID_COLS} spacing="md">
        <BillingNavCard icon={<IconLedger />} title={t('billing.overview.cards.ledger.title')} hint={t('billing.overview.cards.ledger.hint')} to="/billing/ledger" />
        <BillingNavCard icon={<IconInvoice />} title={t('billing.overview.cards.invoices.title')} hint={t('billing.overview.cards.invoices.hint')} to="/billing/invoices" />
        <BillingNavCard icon={<IconLayers />} title={t('billing.overview.cards.subscriptions.title')} hint={t('billing.overview.cards.subscriptions.hint')} to="/billing/subscriptions" />
        <BillingNavCard icon={<IconChart />} title={t('billing.overview.cards.usage.title')} hint={t('billing.overview.cards.usage.hint')} to="/billing/usage" />
        <BillingNavCard icon={<IconLedger />} title={t('billing.overview.cards.walletAlerts.title')} hint={t('billing.overview.cards.walletAlerts.hint')} to="/billing/wallet-alerts" />
        <BillingNavCard icon={<IconLayers />} title={t('billing.overview.cards.plans.title')} hint={t('billing.overview.cards.plans.hint')} to="/billing/plans" />
        <BillingNavCard icon={<IconTag />} title={t('billing.overview.cards.skuRates.title')} hint={t('billing.overview.cards.skuRates.hint')} to="/billing/sku-rates" />
        <BillingNavCard icon={<IconSpark />} title={t('billing.overview.cards.aiModels.title')} hint={t('billing.overview.cards.aiModels.hint')} to="/billing/ai-models" />
        <BillingNavCard icon={<IconGlobe />} title={t('billing.overview.cards.languages.title')} hint={t('billing.overview.cards.languages.hint')} to="/billing/languages" />
        <BillingNavCard icon={<IconGateway />} title={t('billing.overview.cards.gateways.title')} hint={t('billing.overview.cards.gateways.hint')} to="/billing/itn" />
      </SimpleGrid>
    </Stack>
  );
}
